import { useState, useEffect } from "react";
import axios, { getApiUrl } from "@/lib/api";
import { Loader2, Layers, Gamepad2, Calendar, Check, AlertTriangle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Switch } from "@/components/ui/switch";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import PlatformBadge, { SystemBadge } from "@/components/PlatformBadge";
import { toast } from "sonner";

const API = `${getApiUrl()}/api/master`;

/**
 * Scans the MDB for same-named games and lets an editor link them into version
 * groups in one pass. Shows name, release year, platforms, systems and cover
 * art so the editor can spot same-named-but-different games (different art).
 */
export default function AutoLinkVersionsDialog({ open, onOpenChange, onApplied }) {
  const [loading, setLoading] = useState(false);
  const [applying, setApplying] = useState(false);
  const [groups, setGroups] = useState([]);
  const [selected, setSelected] = useState(new Set()); // group keys checked
  const [excluded, setExcluded] = useState(new Set()); // "<groupKey>::<gameId>" individual titles opted out
  const [includeLinked, setIncludeLinked] = useState(false);

  const exKey = (groupKey, id) => `${groupKey}::${id}`;
  const isIncluded = (groupKey, id) => !excluded.has(exKey(groupKey, id));
  const includedGames = (g) => g.games.filter(v => isIncluded(g.key, v.id));
  const toggleGameIncluded = (groupKey, id) => {
    setExcluded(prev => {
      const next = new Set(prev);
      const k = exKey(groupKey, id);
      next.has(k) ? next.delete(k) : next.add(k);
      return next;
    });
  };

  const load = (incLinked) => {
    setLoading(true);
    axios.get(`${API}/version-link/suggestions`, { params: { include_linked: incLinked, limit: 400 } })
      .then(r => {
        const gs = r.data.groups || [];
        setGroups(gs);
        setExcluded(new Set());
        // Default-select every group that isn't already fully linked.
        setSelected(new Set(gs.filter(g => !g.fullyLinked).map(g => g.key)));
      })
      .catch(err => toast.error(err.response?.data?.detail || "Failed to load suggestions"))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    if (open) load(includeLinked);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const toggleIncludeLinked = (val) => {
    setIncludeLinked(val);
    load(val);
  };

  const toggleGroup = (key) => {
    setSelected(prev => {
      const next = new Set(prev);
      next.has(key) ? next.delete(key) : next.add(key);
      return next;
    });
  };

  const selectAll = () => setSelected(new Set(groups.map(g => g.key)));
  const selectNone = () => setSelected(new Set());

  const handleApply = async () => {
    const chosen = groups.filter(g => selected.has(g.key));
    if (chosen.length === 0) { toast.error("Select at least one group"); return; }
    // Build id lists from only the included (not opted-out) titles.
    const groupLists = chosen.map(g => includedGames(g).map(x => x.id));
    const skipped = groupLists.filter(ids => ids.length < 2).length;
    const valid = groupLists.filter(ids => ids.length >= 2);
    if (valid.length === 0) {
      toast.error("Each group needs at least 2 titles to link. Re-include some titles.");
      return;
    }
    setApplying(true);
    try {
      const r = await axios.post(`${API}/version-link/apply`, { groups: valid });
      let msg = r.data?.message || "Groups linked";
      if (skipped > 0) msg += ` (${skipped} skipped — fewer than 2 titles left)`;
      toast.success(msg);
      onApplied?.();
      onOpenChange(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to link groups");
    } finally {
      setApplying(false);
    }
  };

  const selectedCount = selected.size;
  // Groups that will actually link (selected AND >=2 included titles).
  const linkableCount = groups.filter(g => selected.has(g.key) && includedGames(g).length >= 2).length;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[#0f0f0f] border-white/10 max-w-3xl max-h-[88vh] flex flex-col">
        <DialogHeader>
          <DialogTitle className="text-white flex items-center gap-2">
            <Layers className="w-5 h-5 text-cyan-400" />
            Auto-Link Duplicate Versions
          </DialogTitle>
          <DialogDescription className="text-white/40 text-xs">
            Scan the Master Database for games that share the exact same name and link them into version groups.
          </DialogDescription>
        </DialogHeader>

        <div className="flex items-center justify-between flex-wrap gap-2 border-b border-white/10 pb-3">
          <p className="text-white/40 text-xs max-w-md">
            Games sharing the exact same name are grouped below. Review each group — <span className="text-amber-300">different box art may mean a different game</span> — then link the ones you want. Primary defaults to the oldest record (changeable later per-game).
          </p>
          <div className="flex items-center gap-2">
            <span className="text-white/50 text-xs">Show already-linked</span>
            <Switch
              data-testid="autolink-include-linked"
              checked={includeLinked}
              onCheckedChange={toggleIncludeLinked}
              className="data-[state=checked]:bg-cyan-600"
            />
          </div>
        </div>

        <div className="flex items-center gap-2 py-2">
          <span className="text-white/60 text-xs font-medium">{groups.length} group(s) · {selectedCount} selected</span>
          <Button size="sm" variant="ghost" className="h-6 px-2 text-[11px] text-white/50 hover:text-white" onClick={selectAll} data-testid="autolink-select-all">Select all</Button>
          <Button size="sm" variant="ghost" className="h-6 px-2 text-[11px] text-white/50 hover:text-white" onClick={selectNone} data-testid="autolink-select-none">Select none</Button>
        </div>

        <div className="flex-1 overflow-y-auto space-y-3 pr-1">
          {loading ? (
            <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-white/30" /></div>
          ) : groups.length === 0 ? (
            <div className="text-center py-12 text-white/40 text-sm" data-testid="autolink-empty">
              No duplicate-named games to link{includeLinked ? "" : " — toggle \"Show already-linked\" to review existing groups"}.
            </div>
          ) : (
            groups.map((g) => {
              const isChecked = selected.has(g.key);
              return (
                <div
                  key={g.key}
                  data-testid={`autolink-group-${g.key}`}
                  className={`rounded-lg border p-3 transition-colors ${isChecked ? "border-cyan-500/40 bg-cyan-500/5" : "border-white/5 bg-white/[0.02]"}`}
                >
                  <label className="flex items-center gap-2 cursor-pointer mb-2">
                    <Checkbox
                      checked={isChecked}
                      onCheckedChange={() => toggleGroup(g.key)}
                      data-testid={`autolink-check-${g.key}`}
                      className="border-white/30 data-[state=checked]:bg-cyan-600 data-[state=checked]:border-cyan-600"
                    />
                    <span className="text-white text-sm font-medium truncate">{g.name}</span>
                    {(() => { const inc = includedGames(g).length; return (
                      <span className="text-white/40 text-xs">({inc}{inc !== g.count ? ` of ${g.count}` : ""} versions)</span>
                    ); })()}
                    {g.fullyLinked && (
                      <span className="text-[10px] px-1.5 py-0.5 rounded bg-emerald-500/20 text-emerald-300 border border-emerald-500/30 flex items-center gap-1">
                        <Check className="w-2.5 h-2.5" /> already linked
                      </span>
                    )}
                  </label>
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 pl-6">
                    {g.games.map((v) => {
                      const yr = (v.releaseDate || "").match(/\d{4}/)?.[0];
                      const sys = v.primarySystem || (v.systems && v.systems[0]);
                      const inc = isIncluded(g.key, v.id);
                      return (
                        <div
                          key={v.id}
                          data-testid={`autolink-game-${g.key}-${v.id}`}
                          className={`relative flex gap-2 p-1.5 rounded border transition-opacity ${inc ? "bg-black/30 border-white/5" : "bg-black/20 border-white/5 opacity-40"}`}
                        >
                          <label className="absolute top-1 right-1 z-10 cursor-pointer" title={inc ? "Exclude this title from the group" : "Include this title"}>
                            <Checkbox
                              checked={inc}
                              onCheckedChange={() => toggleGameIncluded(g.key, v.id)}
                              data-testid={`autolink-game-check-${v.id}`}
                              className="h-4 w-4 bg-black/60 border-white/40 data-[state=checked]:bg-cyan-600 data-[state=checked]:border-cyan-600"
                            />
                          </label>
                          <div className="w-10 h-14 shrink-0 rounded overflow-hidden bg-black/50">
                            {v.boxArtUrl ? (
                              <img src={v.boxArtUrl} alt="" className="w-full h-full object-cover" />
                            ) : (
                              <div className="w-full h-full flex items-center justify-center"><Gamepad2 className="w-4 h-4 text-white/20" /></div>
                            )}
                          </div>
                          <div className="min-w-0 flex-1 pr-4">
                            <div className="text-white/80 text-[11px] leading-tight line-clamp-2">{v.name}</div>
                            <div className="flex flex-wrap items-center gap-1 mt-1">
                              {(v.platforms || []).slice(0, 2).map(p => <PlatformBadge key={p} platform={p} size="sm" />)}
                              {sys && <SystemBadge system={sys} size="sm" />}
                            </div>
                            {yr && (
                              <div className="flex items-center gap-1 text-[10px] text-white/40 mt-0.5">
                                <Calendar className="w-2.5 h-2.5" />{yr}
                              </div>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            })
          )}
        </div>

        <div className="flex items-center justify-between gap-2 border-t border-white/10 pt-3">
          <div className="flex items-center gap-1.5 text-white/40 text-xs">
            <AlertTriangle className="w-3.5 h-3.5 text-amber-400" />
            Only link records that are truly the same game.
          </div>
          <div className="flex gap-2">
            <Button variant="ghost" onClick={() => onOpenChange(false)} className="text-white/60 hover:text-white">Cancel</Button>
            <Button
              data-testid="autolink-apply"
              onClick={handleApply}
              disabled={applying || linkableCount === 0}
              className="bg-cyan-600 hover:bg-cyan-700"
            >
              {applying ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Layers className="w-4 h-4 mr-2" />}
              Link {linkableCount} group{linkableCount === 1 ? "" : "s"}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
